import React from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { FullscreenLayout } from '@/components/layout/FullscreenLayout';
import { OptionConfiguratorProvider, useOptionConfigurator } from '../OptionConfiguratorContext';
import { ApiMutationProvider } from '../ApiMutationProvider';
import { OptionConfiguratorTabs } from '../OptionConfiguratorTabs/OptionConfiguratorTabs';
import { MobileConfiguratorActions } from '../MobileConfiguratorActions';
import { MobileActionsProvider } from '../MobileActionsContext';
import { Room } from '../Room';
import { buildConfiguratorUrl } from '../utils/buildConfiguratorUrl';
import { IMMERSIVE_STYLE_VARS } from '../immersiveStyleVars';

interface ImmersiveConfiguratorProps {
    residentialUnitId: number;
}

// Authenticated, full-screen immersive configurator (the "options 2.0" experience inside the real
// buyer workflow). Reuses the standard data + mutation providers — so selections persist to the
// backend and deadline/lock rules apply — but presents the showroom's full-bleed two-tier layout
// (`immersive` flag). Mounted at `/cm/options/configurator`.
export const ImmersiveConfigurator: React.FC<ImmersiveConfiguratorProps> = ({ residentialUnitId }) => {
    const match = useRouteMatch();
    // No params in this prefix, so `match.url === match.path`; strip any trailing slash so the child
    // route patterns and the provider's base-pathname comparison match the real URL.
    const baseUrl = (match.url ?? '').replace(/\/$/, '');

    // Render full-screen via the shared layout: it hides the global header, suppresses body scroll,
    // injects the immersive CSS variables, and (because `overlay`) portals a fixed overlay to <body>
    // so it also covers the always-rendered buyer sidebar (rubus menu at z-index 1000).
    return (
        <FullscreenLayout overlay styleVars={IMMERSIVE_STYLE_VARS}>
            <OptionConfiguratorProvider
                residentialUnitId={residentialUnitId}
                basePath={baseUrl}
                baseUrl={baseUrl}
                immersive
                multiDeadline>
                <ApiMutationProvider>
                    <ImmersiveConfiguratorBody baseUrl={baseUrl} />
                </ApiMutationProvider>
            </OptionConfiguratorProvider>
        </FullscreenLayout>
    );
};

// Inner body — lives inside the providers so it can read the resolved deadline/rooms. Mirrors
// ShowroomConfigurator's routing (Room only; styles are picked in-room via the Styles section +
// modal), with a `/deadline/:id` -> first-room hop because the provider redirects base -> /deadline/:id.
const ImmersiveConfiguratorBody: React.FC<{ baseUrl: string }> = ({ baseUrl }) => {
    const { configuratorData, projectData, selectedDeadlineId, rooms } = useOptionConfigurator();

    // Where the Back button returns to: the standard options page (drop the `/configurator` segment).
    const exitBaseUrl = baseUrl.replace(/\/configurator$/, '');
    const backTo = buildConfiguratorUrl({ baseUrl: exitBaseUrl, deadlineId: selectedDeadlineId });

    const isLoading = !configuratorData && !projectData;
    if (isLoading) return null;

    // The immersive two-tier view is a v2-only presentation; legacy projects fall back to the
    // standard options page.
    if (projectData?.useLegacyOptionsView) {
        return <Redirect to={exitBaseUrl} />;
    }

    const firstRoomId = rooms[0]?.roomId;

    return (
        <MobileActionsProvider>
            <div className="fit-height" style={{ width: '100%' }}>
                <OptionConfiguratorTabs mode="immersive" backTo={backTo} />
            {/* The body suppresses page scroll, so the routed content scrolls itself (Room scrolls
                inside its own panel). */}
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Switch>
                    <Route exact path={`${baseUrl}/deadline/:deadlineId/room/:roomId`} component={Room} />
                    {/* The provider redirects base -> /deadline/:id; from there land in the first room. */}
                    <Route exact path={`${baseUrl}/deadline/:deadlineId`}>
                        {firstRoomId != null && selectedDeadlineId != null ? (
                            <Redirect
                                to={buildConfiguratorUrl({
                                    baseUrl,
                                    deadlineId: selectedDeadlineId,
                                    segment: `room/${firstRoomId}`,
                                })}
                            />
                        ) : null}
                    </Route>
                </Switch>
            </div>
                <MobileConfiguratorActions mode="immersive" backTo={backTo} />
            </div>
        </MobileActionsProvider>
    );
};
